import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// Editor de memoria del Asistente APS: ver / agregar / editar / desactivar / eliminar.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Stack, Paper, TextField, Button, IconButton, Chip, Switch,
  MenuItem, CircularProgress, Tooltip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import { api } from '../../services/api';

const SCOPE_LABEL = { user: i18n.t('hardcoded.components_ai_aimemoryeditor.personal'), company: i18n.t('navbar.company'), system: i18n.t('hardcoded.components_ai_aimemoryeditor.sistema') };

export default function AIMemoryEditor({ isManager = false }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newValue, setNewValue] = useState('');
  const [newScope, setNewScope] = useState('user');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.ai.listMemory(); setItems(r.data || []); }
    catch (e) { setItems([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!newValue.trim()) return;
    setSaving(true);
    try {
      const r = await api.ai.createMemory({ value: newValue.trim(), scope: newScope });
      setItems((p) => [r.data, ...p]);
      setNewValue('');
    } catch (e) {
      alert(e?.response?.data?.detail || t('hardcoded.components_ai_aimemoryeditor.no_se_pudo_guardar_la'));
    } finally { setSaving(false); }
  };

  const toggleActive = async (m) => {
    try { const r = await api.ai.updateMemory(m.id, { active: !m.active }); setItems((p) => p.map((x) => x.id === m.id ? r.data : x)); }
    catch (e) { /* noop */ }
  };

  const saveValue = async (m, value) => {
    try { const r = await api.ai.updateMemory(m.id, { value }); setItems((p) => p.map((x) => x.id === m.id ? r.data : x)); }
    catch (e) { alert(e?.response?.data?.detail || t('hardcoded.components_ai_aimemoryeditor.no_se_pudo_actualizar')); }
  };

  const remove = async (m) => {
    if (!window.confirm(t('hardcoded.components_ai_aimemoryeditor.eliminar_esta_memoria'))) return;
    try { await api.ai.deleteMemory(m.id); setItems((p) => p.filter((x) => x.id !== m.id)); }
    catch (e) { alert(e?.response?.data?.detail || t('hardcoded.components_ai_aimemoryeditor.no_se_pudo_eliminar')); }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('hardcoded.components_ai_aimemoryeditor.la_ia_usa_estas_preferencias')}</Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{i18n.t('hardcoded.components_ai_aimemoryeditor.agregar_preferencia')}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
          <TextField fullWidth size="small" placeholder={t('hardcoded.components_ai_aimemoryeditor.ej_muestrame_primero_los_tickets')}
            value={newValue} onChange={(e) => setNewValue(e.target.value)} multiline maxRows={3} />
          <TextField select size="small" label={t('hardcoded.components_ai_aimemoryeditor.alcance')} value={newScope} onChange={(e) => setNewScope(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="user">{i18n.t('hardcoded.components_ai_aimemoryeditor.personal')}</MenuItem>
            {isManager && <MenuItem value="company">{i18n.t('navbar.company')}</MenuItem>}
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} onClick={add} disabled={saving || !newValue.trim()}>{t('common.add')}</Button>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">{t('hardcoded.components_ai_aimemoryeditor.aun_no_hay_memorias_guardadas')}</Typography>
      ) : (
        <Stack spacing={1}>
          {items.map((m) => <MemoryRow key={m.id} m={m} isManager={isManager}
            onToggle={() => toggleActive(m)} onSave={(v) => saveValue(m, v)} onDelete={() => remove(m)} />)}
        </Stack>
      )}
    </Box>
  );
}

function MemoryRow({ m, isManager, onToggle, onSave, onDelete }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(m.value);
  const [dirty, setDirty] = useState(false);
  const canEdit = m.editable && (m.scope === 'user' || (m.scope === 'company' && isManager));

  return (
    <Paper variant="outlined" sx={{ p: 1.5, opacity: m.active ? 1 : 0.55 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Chip size="small" label={SCOPE_LABEL[m.scope] || m.scope}
          color={m.scope === 'company' ? 'primary' : m.scope === 'system' ? 'default' : 'secondary'} sx={{ height: 20 }} />
        {m.category && <Chip size="small" variant="outlined" label={m.category} sx={{ height: 20 }} />}
        <Box sx={{ flex: 1 }} />
        <Tooltip title={m.active ? t('hardcoded.components_ai_aimemoryeditor.activa') : t('hardcoded.components_ai_aimemoryeditor.inactiva')}>
          <Switch size="small" checked={m.active} onChange={onToggle} disabled={!canEdit} />
        </Tooltip>
        {canEdit && (
          <IconButton size="small" color="error" onClick={onDelete}><DeleteOutlineIcon fontSize="small" /></IconButton>
        )}
      </Stack>
      <TextField fullWidth size="small" multiline maxRows={4} value={value}
        disabled={!canEdit}
        onChange={(e) => { setValue(e.target.value); setDirty(e.target.value !== m.value); }} />
      {dirty && canEdit && (
        <Box sx={{ mt: 1, textAlign: 'right' }}>
          <Button size="small" startIcon={<SaveIcon />} onClick={() => { onSave(value); setDirty(false); }}>{i18n.t('common.save')}</Button>
        </Box>
      )}
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
        {i18n.t('hardcoded.components_ai_aimemoryeditor.actualizado')}{m.updated_at ? new Date(m.updated_at).toLocaleString('es-MX') : '—'}
      </Typography>
    </Paper>
  );
}
